import { useEffect, useRef, useState } from 'react';

/**
 * One anchored note thread (v2 design §3). The server is the boundary: this component
 * renders whatever notes the payload carried (family tier and above only) and, when the
 * link may write, POSTs through the parent's onSubmit. Bodies render as text through
 * React's default escaping - there is no HTML path.
 */

export interface PortalNoteView {
  id: string;
  authorName: string;
  body: string;
  createdAt: string;
}

const AUTHOR_KEY = 'portal-note-author';

/**
 * localStorage is a convenience here, never a dependency: iOS Safari in private mode THROWS
 * on both read and write. A throw must not blank the portal, and must not cost the client the
 * note they just saved - hence the two guards rather than one try around the save.
 */
const readAuthor = (): string => {
  try {
    return localStorage.getItem(AUTHOR_KEY) ?? '';
  } catch {
    return '';
  }
};

const rememberAuthor = (name: string): void => {
  try {
    localStorage.setItem(AUTHOR_KEY, name);
  } catch {
    /* private mode: the name simply is not remembered next time. */
  }
};

const fmtWhen = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function PortalNotes({
  notes,
  canWrite,
  onSubmit,
  compact = false,
  addLabel,
  openRequest,
}: {
  notes: PortalNoteView[];
  canWrite: boolean;
  onSubmit: (authorName: string, body: string) => Promise<boolean>;
  compact?: boolean;
  /*
    What the "add" chip says when there is nothing here yet. Defaults to "+ Add note".

    It exists because the open items now carry a thread PER ITEM under a section that also has one
    (VP8.1a), and two identical chips a few pixels apart do not tell a reader which one they are
    writing on. His own ruling, from the documents fold: A CONTROL MUST NAME ITS CONTENTS. The count
    label ("Notes (2)") needs no help - a populated thread sits under the thing it belongs to.
  */
  addLabel?: string;
  /*
    VP8.5: a number that CHANGES when something outside asks this thread to open - the map popup's "Ask Apex".
    Each new value opens the form and puts the cursor in the note; undefined asks nothing.
  */
  openRequest?: number;
}) {
  const [open, setOpen] = useState(false);
  const noteRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (openRequest !== undefined) setOpen(true);
  }, [openRequest]);
  useEffect(() => {
    // preventScroll: the page is already travelling to this card smoothly; a jump would fight it.
    if (open && openRequest !== undefined) noteRef.current?.focus({ preventScroll: true });
  }, [open, openRequest]);
  const [author, setAuthor] = useState(readAuthor);
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!canWrite && notes.length === 0) return null;

  const label = notes.length > 0 ? `Notes (${notes.length})` : (addLabel ?? '+ Add note');

  const save = async () => {
    const name = author.trim();
    const text = body.trim();
    if (!name || !text) {
      setError(!name ? 'Please add your name.' : 'The note is empty.');
      return;
    }
    setSaving(true);
    setError('');
    const ok = await onSubmit(name, text);
    setSaving(false);
    if (ok) {
      rememberAuthor(name);
      setBody('');
    } else {
      setError("Couldn't save your note — please try again.");
    }
  };

  return (
    <div className={`portal-notes${compact ? ' portal-notes-compact' : ''}`}>
      <button type="button" className="portal-notes-chip" onClick={() => setOpen((o) => !o)}>
        {label}
      </button>
      {/*
        The thread stays MOUNTED once it has content and is hidden with CSS while the chip is
        closed, because the print stylesheet un-hides it: a thread that only EXISTS while open
        can never print, and on paper a note is content. The form stays open-gated - it is a
        control, and print drops controls either way.
      */}
      {(notes.length > 0 || open) && (
        <div className="portal-notes-thread" data-open={open ? 'true' : 'false'}>
          {notes.map((n) => (
            <div className="portal-notes-item" key={n.id}>
              <p className="portal-notes-meta">
                {n.authorName}
                {fmtWhen(n.createdAt) && <span> · {fmtWhen(n.createdAt)}</span>}
              </p>
              <p className="portal-notes-body">{n.body}</p>
            </div>
          ))}
          {open && canWrite && (
            <div className="portal-notes-form">
              <label>
                Your name
                <input
                  value={author}
                  maxLength={80}
                  onChange={(e) => setAuthor(e.target.value)}
                  placeholder="Who is writing this?"
                />
              </label>
              <label>
                Note
                <textarea
                  ref={noteRef}
                  value={body}
                  maxLength={2000}
                  rows={3}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Add your input for the family and your Apex broker…"
                />
              </label>
              {error && <p className="portal-notes-error">{error}</p>}
              <button type="button" disabled={saving} onClick={save}>
                {saving ? 'Saving…' : 'Save note'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
